import React, { useState, useMemo, createElement } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Tag, theme } from 'antd';
import {
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { getRoleMenuItems, ROLE_NAMES } from '../config/roleConfig';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// Convert roleConfig items (with component references) to Ant Design menu items (with JSX icons)
const buildAntMenuItems = (items) =>
  items.map((item) => {
    const base = {
      key: item.key,
      icon: item.icon ? createElement(item.icon) : undefined,
      label: item.label,
    };
    if (item.children) {
      base.children = buildAntMenuItems(item.children);
    }
    return base;
  });

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { token: themeToken } = theme.useToken();

  const roleId = user?.role_id;
  const roleName = ROLE_NAMES[roleId] || 'Foydalanuvchi';

  const menuItems = useMemo(() => {
    const raw = getRoleMenuItems(roleId);
    return buildAntMenuItems(raw);
  }, [roleId]);

  // Collect all leaf keys for openKeys default
  const defaultOpenKeys = useMemo(() => {
    return menuItems.filter((i) => i.children).map((i) => i.key);
  }, [menuItems]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: user?.name || 'Profil', disabled: true },
      { key: 'role', label: <Tag color="blue" style={{ margin: 0 }}>{roleName}</Tag>, disabled: true },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Chiqish', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') handleLogout();
    },
  };

  const pathKey = '/' + location.pathname.split('/')[1];
  const selectedKey = pathKey === '/' ? '/' : pathKey;

  // For admin submenus, also select prefixed keys that match current route
  const selectedKeys = useMemo(() => {
    const keys = [selectedKey];
    const findPrefixedKeys = (items) => {
      (items || []).forEach((item) => {
        if (item.children) {
          item.children.forEach((child) => {
            if (child.key && child.key.includes(':')) {
              const route = child.key.split(':').pop();
              if (route === selectedKey) keys.push(child.key);
            }
          });
        }
      });
    };
    findPrefixedKeys(menuItems);
    return keys;
  }, [selectedKey, menuItems]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        onBreakpoint={(broken) => setCollapsed(broken)}
        width={230}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: themeToken.colorBgContainer,
          borderRight: `1px solid ${themeToken.colorBorderSecondary}`,
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          borderBottom: `1px solid ${themeToken.colorBorderSecondary}`,
          padding: '8px 12px',
        }}>
          <Text strong style={{ fontSize: collapsed ? 16 : 18, color: themeToken.colorPrimary, lineHeight: 1.2 }}>
            {collapsed ? 'T' : 'Tuyona Admin'}
          </Text>
          {!collapsed && (
            <Text style={{ fontSize: 11, color: themeToken.colorTextSecondary }}>
              {roleName}
            </Text>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={defaultOpenKeys}
          items={menuItems}
          onClick={({ key }) => {
            // Keys may have prefix like 'stat:/providers' — extract the route part
            const route = key.includes(':') ? key.split(':').pop() : key;
            navigate(route);
          }}
          style={{ borderRight: 0, marginTop: 8 }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 230, transition: 'all 0.2s' }}>
        <Header style={{
          padding: '0 24px',
          background: themeToken.colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${themeToken.colorBorderSecondary}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <div
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 18, cursor: 'pointer', color: themeToken.colorText }}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
          <Dropdown menu={userMenu} placement="bottomRight">
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text>{user?.name}</Text>
              <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{roleName}</Tag>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: themeToken.colorPrimary }} />
            </div>
          </Dropdown>
        </Header>
        <Content style={{ margin: 24, minHeight: 280 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
